import { Link, useParams } from "react-router";
import { Printer } from "lucide-react";
import { buttonClass } from "@/design/buttons";
import { DataUnavailable } from "@/design/DataUnavailable";
import { CARE_COPY } from "@/design/careCopy";
import { MealPlanReader, PlanPhaseBadge } from "@/design/MealPlanReader";
import type { ConsumerPlanView } from "@/design/mealPlanViews";
import { Banner, PageShell } from "@/design/ui";
import { ConsumerPage } from "~/components/ConsumerChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { useQuery } from "~/lib/useQuery";
import { NotFoundScreen } from "~/screens/NotFoundScreen";

/**
 * One of the Consumer's meal plans (R23, H53): docs/ux/screens/ConsumerMealPlanScreen.md.
 * The list of them is a section of My care (H55), in CareScreen.tsx.
 *
 * **Read only, and only what was shared.** The query beneath never selects a
 * Draft, so there is nothing here to hide: a plan the clinic is still writing
 * does not exist as far as this screen can tell, and its id is a 404 like any
 * other id that is not theirs.
 */

/**
 * `/meal-plans/:planId`, which is also where the WhatsApp link lands.
 *
 * Printing is the browser's own: the plan is plain document structure and the
 * chrome is `print:hidden`, so "Print" gives a clean page without a generated
 * PDF (H53 part 4).
 */
export function ConsumerMealPlanScreen() {
  const { planId = "" } = useParams();
  const view = useQuery<ConsumerPlanView>(planId ? `/meal-plans/${planId}` : null);
  if (view.failure === "meal_plan_not_found") return <NotFoundScreen />;

  return (
    <ConsumerPage>
      <PageShell>
        <div className="mx-auto w-full max-w-2xl">
          <Link
            to="/care"
            className="mb-4 inline-block text-sm font-medium text-accent print:hidden"
          >
            {CARE_COPY.back}
          </Link>
          {view.loading && !view.data ? (
            <ScreenPending />
          ) : view.failure || !view.data ? (
            <DataUnavailable
              configured={view.failure !== "not_configured"}
              what="this meal plan"
            />
          ) : (
            <>
              <header className="mb-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-ink">{view.data.title}</h1>
                    <p className="mt-1 text-sm text-muted">
                      From {view.data.businessName} · {view.data.dates}
                    </p>
                    <p className="text-xs text-muted">
                      {view.data.sharedOn}
                      {view.data.updatedOn ? ` · ${view.data.updatedOn}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 print:hidden">
                    <PlanPhaseBadge
                      phase={view.data.phase}
                      label={view.data.phaseLabel}
                    />
                    <button
                      type="button"
                      onClick={() => window.print()}
                      className={buttonClass("secondary", "min-h-9 py-1.5 text-sm")}
                    >
                      <Printer className="size-4" aria-hidden strokeWidth={1.8} />
                      Print
                    </button>
                  </div>
                </div>
              </header>
              {view.data.phase === "archived" && (
                <Banner tone="info">
                  {view.data.businessName} has marked this plan as no longer
                  active. It stays here for your reference.
                </Banner>
              )}
              <MealPlanReader body={view.data.body} />
              <p className="mt-6 text-xs text-muted">
                Questions about this plan? Ask {view.data.businessName}: they
                wrote it for you and can explain or change it.
              </p>
            </>
          )}
        </div>
      </PageShell>
    </ConsumerPage>
  );
}
